import React, { useEffect } from "react";
import {SdfPromoBox, SdfLink, SdfIcon, SdfTooltip, SdfIconButton, SdfButton} from "@synerg/react-components";
import { LoadHtmlFromString } from "./LoadHtmlFromString";
import {useSelector} from "react-redux";
import { TableOfContents } from "./TableOfContents";
import { ShouldLoginBox } from "../ShouldLoginBox";
import useMediaQuery from "../../hooks/useMediaQuery";
import { copyToClipboard } from "../../utility/copyToClipboard";
import './relatedGuides.scss';
import {useFetchGuideCmsHtmlMutation} from "../../store";

export function GuideContent({ content, guideId, exportTitle, exportAbstract }) {
	const isLoggedIn = useSelector((state) => {
		return state.auth.isLoggedIn;
	});
	useEffect(()=>{
		window.scrollTo(0, 0);
	},[content])

	const guideType = content?._source?.guideType;
	const isSM = useMediaQuery('(max-width: 600px)')
	const PDF =  (content?.isPublic || isLoggedIn) && content?.pdfLink &&
	<SdfLink className="cursor-pointer no-underline text-action-primary" emphasis="primary"
		external={true}
		href={content.pdfLink} target="_blank">
		Download Guide
	</SdfLink>;

	const getChapterHtml = () => (content?.chaptersSections || [])
		.filter((chapter) => typeof chapter?.content === "string" && chapter.content.trim().length)
		.map((chapter, index) => `
			<section>
				<h2>Chapter ${index + 1}: ${chapter?.title || "Untitled"}</h2>
				<div>${chapter.content}</div>
			</section>
		`)
		.join("\n");

	const htmlContent = getChapterHtml();
	const hasMarkdownSource = htmlContent.trim().length > 0;

	/** Builds the full HTML document (title + summary + chapters) for markdown export. */
	const getFullExportHtml = () => {
		const parts = [];
		if (exportTitle) {
			parts.push(`<h1>${exportTitle}</h1>`);
		}
		if (exportAbstract) {
			parts.push(`<h2>Summary</h2><p>${exportAbstract}</p>`);
		}
		parts.push(htmlContent);
		return parts.join("\n");
	};
	const [convertToMarkdown] = useFetchGuideCmsHtmlMutation();

	const downloadGuideHtml2MD = async () => {
		if (!hasMarkdownSource) {
			console.warn("No HTML available for markdown conversion");
			return;
		}

		try {
			const response = await convertToMarkdown({
				title: guideId || content?.title || "guide",
				html: getFullExportHtml(),
			}).unwrap();
			const markdown = response?.markdown || response?.data?.markdown || "";
			if (!markdown) {
				console.warn("No markdown returned from conversion");
				return;
			}

			const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
			const url = window.URL.createObjectURL(blob);
			const anchor = document.createElement("a");
			anchor.href = url;
			anchor.download = `${(content?._id || content?.title || "guide").replace(/\s+/g, "-")}.md`;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error converting to markdown:", error);
		}
	};

	const MARKDOWN_BUTTON = hasMarkdownSource ? (
		<SdfButton
			type="button"
			onClick={downloadGuideHtml2MD}
			className="text-action-primary text-left"
		>
			Download Markdown
		</SdfButton>
	) : null;

	return (
		<>
			<div className="md:mx-2">
				<>{
					<div className="flex">
						<div className='w-full overflow-hidden'>
							<div className="flex mt-1">
								<section id="guideContent" className="pb-3 w-full">
									<div id="guideTitle" className="text-3xl font-bold my-4">{content.title}</div>
									<SdfPromoBox
										size='sm'
										spacing="default"
										layout="horizontal"
										align='left'
										variant='filled'
										header='Summary'
										message={content.abstract}
										illustration-name='documents'
									/>
									{
										isSM && <div className="mt-6 flex flex-col gap-2">{PDF}{MARKDOWN_BUTTON}</div>
									}
									{!content?.isPublic && !isLoggedIn &&
                            <div className="flex pt-4 flex-col justify-center items-center">
                                <ShouldLoginBox />
                            </div>
                  }
                  <div className="content-wrap">
									<div id="doc-preview">
										{content?.chaptersSections.map((chapter, index) => {
											return (<div key={index}>
										   <div className={`text-tertiary font-medium mt-8 simple h2-${index+1}`} chapter-id={`${index + 1}`}>
												CHAPTER {index + 1}
											</div>
											<div className="flex items-center">
											 <h2> {chapter.title}</h2>
											 <SdfTooltip tooltipId="action-link" attachmentPoint="right" trigger="click" className="mt-1 ml-2">
                            <SdfIconButton slot="tooltip-target"  onClick = {()=> copyToClipboard(`${window.location.href}/chapter/${index + 1}`)}>
                                <SdfIcon icon="action-link"  className="text-xl cursor-pointer" ></SdfIcon>
                            </SdfIconButton>
                            <span>
												Copied link
                            </span>
                        </SdfTooltip>
										</div>
										<LoadHtmlFromString
											key={index}
											htmlStr={chapter.content}
											className="ml-6"
										/>
										</div>)
										})}
									</div>
									</div>
									<footer id="guideContentFooter"></footer>
								</section>
							</div>
						</div>
						<div className="flex flex-col">
							<div className="flex flex-col sticky" style={{ top: '5.50rem' }}>
								<aside className="md:mx-6">
									{!isSM && <div className="flex flex-col gap-2">{PDF}{MARKDOWN_BUTTON}</div>}
									<TableOfContents
										currentGuideSections={content?.chaptersSections}
										guideType = {guideType}
									/>
								</aside>
							</div>

						</div>
					</div>
				}</>
			</div>
		</>
	)

}
